import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BottomTabBar } from '@/components/layout/BottomTabBar'
import { formatShortThaiDate } from '@krudream/shared'
import { apiUrl } from '@/lib/api'

interface StudentSummary {
  studentId: string
  studentNumber: number
  prefix: string
  fullName: string
  nickname: string | null
  submitted: boolean
  submittedAt: string | null
  missingCount: number
}

interface EventSummary {
  event: {
    id: string
    name: string
    date: string
  }
  totalStudents: number
  submittedCount: number
  students: StudentSummary[]
}

export function OrientationLivePage() {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<EventSummary | null>(null)
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!eventId) return
    fetch(apiUrl(`/api/orientation/events/${eventId}/summary`), { credentials: 'include' })
      .then((r) => r.json())
      .then((res) => {
        if (res.success) setData(res.data)
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }, [eventId])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-400">กำลังโหลด...</div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-400">ไม่พบข้อมูล</div>
      </div>
    )
  }

  const pendingCount = data.totalStudents - data.submittedCount
  const progressPct =
    data.totalStudents > 0
      ? Math.round((data.submittedCount / data.totalStudents) * 100)
      : 0

  const filtered = data.students.filter((s) => {
    if (!search.trim()) return true
    const q = search.trim()
    return (
      s.fullName.includes(q) ||
      (s.nickname && s.nickname.includes(q)) ||
      String(s.studentNumber).includes(q)
    )
  })

  const pending = filtered.filter((s) => !s.submitted)
  const submitted = filtered.filter((s) => s.submitted)

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <button onClick={() => navigate('/settings')} className="text-gray-600">⟨</button>
          <div className="flex-1">
            <h1 className="truncate text-base font-bold text-gray-900">{data.event.name}</h1>
            <p className="text-xs text-gray-400">
              {formatShortThaiDate(new Date(data.event.date))}
            </p>
          </div>
          <button
            onClick={() => navigate(`/orientation/${eventId}/dashboard`)}
            className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600"
          >
            Dashboard
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-3">
        {/* Summary bar */}
        <div className="mb-3 rounded-lg bg-white p-3 shadow-sm">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="text-gray-600">
              submit แล้ว {data.submittedCount} · ยังไม่มา {pendingCount}
            </span>
            <span className="font-semibold text-primary-600">{progressPct}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-primary-500 transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* Search */}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="ค้นหานักเรียน..."
          className="mb-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
        />

        {/* Pending list */}
        {pending.length > 0 && (
          <div className="mb-4">
            <h2 className="mb-2 text-sm font-semibold text-gray-600">
              ยังไม่ submit ({pending.length})
            </h2>
            <div className="space-y-1">
              {pending.map((s) => (
                <button
                  key={s.studentId}
                  onClick={() => navigate(`/orientation/${eventId}/qr/${s.studentId}`)}
                  className="flex w-full items-center gap-3 rounded-lg bg-white px-3 py-2.5 text-left text-sm shadow-sm"
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded border-2 border-gray-300 text-xs" />
                  <span className="flex-1 text-gray-900">
                    {s.studentNumber} · {s.prefix}{s.fullName}
                    {s.nickname && <span className="text-gray-400"> ({s.nickname})</span>}
                  </span>
                  <span className="text-xs text-gray-400">แสกน</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Submitted list */}
        {submitted.length > 0 && (
          <div>
            <h2 className="mb-2 text-sm font-semibold text-gray-600">
              submit แล้ว ({submitted.length})
            </h2>
            <div className="space-y-1">
              {submitted.map((s) => (
                <div
                  key={s.studentId}
                  className="flex w-full items-center gap-3 rounded-lg bg-green-50 px-3 py-2.5 text-sm"
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded bg-green-500 text-xs text-white">
                    ✓
                  </span>
                  <span className="flex-1 text-gray-500">
                    {s.studentNumber} · {s.prefix}{s.fullName}
                    {s.nickname && <span className="text-gray-400"> ({s.nickname})</span>}
                  </span>
                  {s.missingCount > 0 && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                      ขาด {s.missingCount}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {filtered.length === 0 && (
          <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-400 shadow-sm">
            ไม่พบนักเรียน
          </div>
        )}
      </main>

      <BottomTabBar active="more" />
    </div>
  )
}
